import { useState } from "react";
import moment from "moment";
import { determinStatus } from "../../Utils";
import MessagePreview from "../MessagePreview";
import "./MessageScheduleList.css";

const MISSING_MEDIA_ERROR_TEXT = "הודעות לא נשלחו, לא נמצאה תמונת הזמנה.";
const MESSAGE_PURPOSE_LABELS = {
  invitation: "שליחת הזמנה ואישור הגעה",
  unansweredReminder: "תזכורת למי שלא ענה",
  confirmedReminder: "תזכורת לקראת האירוע",
};

const getScheduleDateTime = (value) => {
  const parsed = moment(value, ["YYYY-MM-DD HH:mm", moment.ISO_8601], true);
  if (!parsed.isValid()) return { date: String(value || "-"), time: "-" };
  return {
    date: parsed.format("DD/MM/YYYY"),
    time: parsed.format("HH:mm"),
  };
};

const getMessageDeliveryStats = (message, deliveryRecords, missing = 0) => {
  const messageId = message.messageId || "";
  const results = messageId
    ? (deliveryRecords.messageResults || []).filter((result) => result.messageId === messageId)
    : [];
  const skippedRecords = messageId
    ? (deliveryRecords.skipped || []).filter((record) => record.messageId === messageId)
    : [];
  const stored = message.deliverySummary || {};
  const hasDetails = results.length > 0 || skippedRecords.length > 0 || Object.keys(stored).length > 0;
  const sent = results.length > 0
    ? results.filter((result) => result.status === "sent").length
    : Number(stored.sentCount || 0);
  const failed = results.length > 0
    ? results.filter((result) => result.status === "failed" || result.status === "error").length
    : Number(stored.failedCount || 0);
  const processing = results.length > 0
    ? results.filter((result) => !["sent", "failed", "error"].includes(result.status)).length
    : Number(stored.pendingCount || 0);
  const skippedCredit = skippedRecords.length > 0
    ? skippedRecords.length
    : Math.max(Number(stored.skippedCreditCount || 0), Number(missing || 0));

  return {
    hasDetails,
    targeted: Number(stored.targetedCount ?? message.recipientCount ?? 0),
    sent,
    failed,
    processing,
    skippedCredit,
  };
};

const ChannelIcon = ({ channel }) => (
  <span className={`schedule-list__channel-icon schedule-list__channel-icon--${channel}`} aria-hidden="true">
    {channel === "whatsapp" ? (
      <svg viewBox="0 0 24 24"><path d="M19.1 4.9A9.8 9.8 0 0 0 3.7 16.7L2.3 22l5.4-1.4A9.8 9.8 0 0 0 22 11.9a9.7 9.7 0 0 0-2.9-7Zm-7 15.2a8 8 0 0 1-4.1-1.1l-.3-.2-3.2.8.9-3.1-.2-.3a8 8 0 1 1 6.9 3.9Zm4.4-6c-.2-.1-1.4-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.3-.6.8-.8 1-.1.2-.3.2-.5.1-1.5-.7-2.5-1.3-3.5-3-.3-.5.3-.5.8-1.6.1-.2 0-.4 0-.5l-.8-1.9c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2 3.1 4.9 4.3 1.8.8 2.5.8 3.4.7 1-.2 1.4-1.2 1.6-1.7.2-.5.2-1 .1-1.1-.2-.2-.4-.2-.7-.4Z" /></svg>
    ) : (
      <svg viewBox="0 0 24 24"><path d="M3 5h18v14H3V5Zm2 2v.4l7 5.1 7-5.1V7H5Zm14 10V9.9l-7 5.1-7-5.1V17h14Z" /></svg>
    )}
  </span>
);

const MessageScheduleRow = ({ message, onDelete, disabled, missing, deliveryRecords, invitationImageUrl, hideContactCounts }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isPast = moment(message.timeToSend).isBefore(moment());
  const cancelled = message.status === "cancelled";
  const isError = message.status === "error";
  const channel = message.channel === "whatsapp" ? "whatsapp" : "sms";
  const channelLabel = channel === "whatsapp" ? "וואטסאפ" : "SMS";
  const purposeLabel = message.freeText
    ? "טקסט חופשי"
    : MESSAGE_PURPOSE_LABELS[message.messagePurpose] || "הודעה מותאמת אישית";
  const stats = getMessageDeliveryStats(message, deliveryRecords, missing);
  const scheduleDateTime = getScheduleDateTime(message.timeToSend);
  const queuedSentImageUrl = (deliveryRecords.messageResults || []).find(
    (result) => result.messageId === message.messageId && result.sentInvitationImageUrl
  )?.sentInvitationImageUrl;
  const previewImageUrl = isPast
    ? queuedSentImageUrl || message.sentInvitationImageUrl || ""
    : message.invitationImageUrl || invitationImageUrl;
  const statusLabel = cancelled
    ? "בוטלה"
    : isError
      ? "שגיאה"
      : isPast
        ? stats.failed > 0 || stats.skippedCredit > 0 ? "הושלמה חלקית" : "נשלחה"
        : "מתוזמנת לשליחה";

  return (
    <article className={`schedule-list__row ${cancelled ? "schedule-list__row--cancelled" : ""}`}>
      <ChannelIcon channel={channel} />
      <button
        type="button"
        className="schedule-list__row-toggle"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
      <div className="schedule-list__content">
        <div className="schedule-list__badges">
          <span className={`schedule-list__channel-badge schedule-list__channel-badge--${channel}`}>{channelLabel}</span>
          {cancelled && <span className="schedule-list__state schedule-list__state--cancelled">בוטל</span>}
          {!cancelled && isError && <span className="schedule-list__state schedule-list__state--error">שגיאה</span>}
          {!cancelled && !isError && isPast && <span className="schedule-list__state schedule-list__state--sent">נשלח</span>}
          {!cancelled && !isError && !isPast && <span className="schedule-list__state schedule-list__state--pending">ממתין לשליחה</span>}
        </div>
        <div className="schedule-list__meta">
          <span><strong>מטרה:</strong> {purposeLabel}</span>
          <span><strong>תאריך:</strong> {scheduleDateTime.date}</span>
          <span><strong>שעה:</strong> {scheduleDateTime.time}</span>
          <span><strong>קבוצה:</strong> {determinStatus(message.toGroup)}</span>
        </div>
        {isError && (
          <div className="schedule-list__notice schedule-list__notice--error">
            {message.errorText || MISSING_MEDIA_ERROR_TEXT}
          </div>
        )}
        {!hideContactCounts && missing > 0 && !cancelled && !isPast && (
          <div className="schedule-list__notice schedule-list__notice--warning">
            תזכורות לא ישלחו {missing} הודעות — אין מספיק הודעות {channelLabel}
          </div>
        )}
        {!hideContactCounts && missing > 0 && !cancelled && isPast && (
          <div className="schedule-list__notice schedule-list__notice--error">
            {missing} הודעות לא נשלחו — לא היה מספיק קרדיט {channelLabel}
          </div>
        )}
      </div>
      <span className={`schedule-list__row-arrow ${isOpen ? "schedule-list__row-arrow--open" : ""}`} aria-hidden="true">▼</span>
      </button>
      {!cancelled && !isError && !isPast && (
        <button type="button" className="schedule-list__cancel" disabled={disabled} onClick={onDelete}>
          ביטול
        </button>
      )}
      {isOpen && (
        <div className="schedule-list__details">
          <dl className="schedule-list__details-grid">
            <div><dt>סוג ההודעה</dt><dd>{purposeLabel}</dd></div>
            <div><dt>ערוץ</dt><dd>{channelLabel}</dd></div>
            <div><dt>קבוצת נמענים</dt><dd>{determinStatus(message.toGroup)}</dd></div>
            <div><dt>תאריך</dt><dd>{scheduleDateTime.date}</dd></div>
            <div><dt>שעה</dt><dd>{scheduleDateTime.time}</dd></div>
            <div><dt>סטטוס</dt><dd>{statusLabel}</dd></div>
            {!hideContactCounts && <>
            <div><dt>נמענים מתוכננים</dt><dd>{stats.targeted}</dd></div>
            {stats.hasDetails && <div className="schedule-list__metric schedule-list__metric--success"><dt>נשלחו בהצלחה</dt><dd>{stats.sent}</dd></div>}
            {stats.hasDetails && stats.failed > 0 && <div className="schedule-list__metric schedule-list__metric--failed"><dt>נכשלו</dt><dd>{stats.failed}</dd></div>}
            {stats.hasDetails && <div className="schedule-list__metric schedule-list__metric--warning"><dt>לא נשלחו — חוסר קרדיט</dt><dd>{stats.skippedCredit}</dd></div>}
            {stats.hasDetails && stats.processing > 0 && <div><dt>ממתינות לתוצאה</dt><dd>{stats.processing}</dd></div>}
            {!isPast && <div><dt>ממתינות לשליחה</dt><dd>{stats.targeted}</dd></div>}
            {!isPast && <div><dt>קרדיטים שמורים</dt><dd>{message.recipientCount || 0}</dd></div>}
            </>}
            <div>
              <dt>קישור להזמנה</dt>
              <dd className={message.freeText ? "schedule-list__not-included" : ""}>
                {message.freeText ? "לא יצורף" : message.withLink ? "מצורף" : "לא מצורף"}
              </dd>
            </div>
            {message.freeText && <div><dt>תמונה</dt><dd className="schedule-list__not-included">לא תישלח</dd></div>}
            {(message.messagePurpose === "confirmedReminder" || message.freeText) && (
              <>
                <div>
                  <dt>מספר שולחן</dt>
                  <dd className={message.freeText ? "schedule-list__not-included" : ""}>
                    {message.freeText ? "לא יצורף" : message.includeTable ? "יצורף למי ששובץ" : "לא יצורף"}
                  </dd>
                </div>
                <div>
                  <dt>פרטי הסעה</dt>
                  <dd className={message.freeText ? "schedule-list__not-included" : ""}>
                    {message.freeText ? "לא יצורפו" : message.includeTransportation ? "יצורפו למי שביקש הסעה" : "לא יצורפו"}
                  </dd>
                </div>
              </>
            )}
          </dl>
          {message.transportationDetails && (
            <div className="schedule-list__message-copy">
              <strong>כתובת ושעת יציאת ההסעה</strong>
              <p>{message.transportationDetails}</p>
            </div>
          )}
          {message.message && (
            <MessagePreview
                channel={channel}
                imageUrl={previewImageUrl}
              text={message.message}
              sent={isPast}
              freeTextEnabled={Boolean(message.freeText)}
            />
          )}
          {isPast && channel === "whatsapp" && !previewImageUrl && (
            <div className="schedule-list__notice schedule-list__notice--neutral">
              התמונה המדויקת שנשלחה לא נשמרה עבור הודעה ישנה זו, ולכן לא מוצגת תמונה חלופית.
            </div>
          )}
          {isPast && !stats.hasDetails && (
            <div className="schedule-list__notice schedule-list__notice--neutral">
              להודעה ישנה זו לא נשמר פירוט תוצאות לפי נמען.
            </div>
          )}
        </div>
      )}
    </article>
  );
};

const MessageScheduleList = ({
  messages = [],
  onDelete,
  disabled = false,
  getMissingCount,
  deliveryRecords = { skipped: [], messageResults: [] },
  invitationImageUrl = "",
  hideContactCounts = false,
}) => {
  const [showSentMessages, setShowSentMessages] = useState(false);
  const indexedMessages = messages.map((message, index) => ({ message, index }));
  const scheduledMessages = indexedMessages.filter(({ message }) =>
    moment(message.timeToSend).isSameOrAfter(moment()) && message.status !== "cancelled" && message.status !== "error"
  );
  const sentMessages = indexedMessages.filter(({ index }) =>
    !scheduledMessages.some((scheduled) => scheduled.index === index)
  );

  if (sentMessages.length === 0 && scheduledMessages.length === 0) return null;

  const renderRows = (entries) => (
    <div className="schedule-list__rows">
      {entries.map(({ message, index }) => (
        <MessageScheduleRow
          key={`${message.timeToSend}-${message.channel || "sms"}-${index}`}
          message={message}
          disabled={disabled}
          onDelete={() => onDelete?.(index)}
          missing={getMissingCount ? getMissingCount(message) : 0}
          deliveryRecords={deliveryRecords}
          invitationImageUrl={invitationImageUrl}
          hideContactCounts={hideContactCounts}
        />
      ))}
    </div>
  );

  return (
    <div className="schedule-list-group" dir="rtl">
      {sentMessages.length > 0 && (
        <section className="schedule-list schedule-list--sent">
          <button
            type="button"
            className="schedule-list__header schedule-list__header--toggle"
            aria-expanded={showSentMessages}
            onClick={() => setShowSentMessages((current) => !current)}
          >
            <h2>הודעות שנשלחו</h2>
            <span>{sentMessages.length} הודעות</span>
          </button>
          <button
            type="button"
            className="schedule-list__dropdown-bar"
            aria-label={showSentMessages ? "סגירת הודעות שנשלחו" : "פתיחת הודעות שנשלחו"}
            aria-expanded={showSentMessages}
            onClick={() => setShowSentMessages((current) => !current)}
          >
            <span className={`schedule-list__dropdown-arrow ${showSentMessages ? "schedule-list__dropdown-arrow--open" : ""}`} aria-hidden="true">▼</span>
          </button>
          {showSentMessages && (
            <div className="schedule-list__dropdown-content">
              {renderRows(sentMessages)}
            </div>
          )}
        </section>
      )}

      {scheduledMessages.length > 0 && (
        <section className="schedule-list">
          <div className="schedule-list__header">
            <h2>הודעות מתוזמנות לשליחה</h2>
            <span>{scheduledMessages.length} הודעות</span>
          </div>
          {renderRows(scheduledMessages)}
        </section>
      )}
    </div>
  );
};

export default MessageScheduleList;
